import _ from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ReceiptIcon from '@mui/icons-material/Receipt'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

import CloseIcon from '@mui/icons-material/Close'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import TextInputController from '@/components/ui/inputs/TextInputController'

import { ExpertiseService, ExpertiseStatus } from '@/core/models/expertise.model'
import { useDiagnoseExpertiseMutation, useGetExpertiseQuery, useUpdateExpertiseMutation } from '@/core/services/expertise'
import { ChatModal } from '@/features/medconnect-agent/ui/ChatModal'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { MAIN_HEADER_HEIGHT } from '@/layouts/main'

import GenerateFspDialog from '@/pages/diagnostic/components/GenerateFSPDialog'
import NotFoundPage from '@/pages/error/NotFoundPage'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { generateFSP, selectExpertiseCreateRequestTracker } from '@/redux/reducers/expertise.reducer'

import { redirectTo } from '@/utils/expertise'
import { isStaff } from '@/utils/type-guards'

import ExpertiseHeader from './components/ExpertiseHeader'
import Documents from './containers/Documents'
import HealthDataList from './containers/HealthDataList'
import InitiatorInfos from './components/InitiatorInfo'
import Messenger from './containers/Messenger'
import PatientInfosCard from './components/PatientInfosCard'
import ExpertiseForms from './containers/ExpertiseForms'
import { MedicalActsSection } from '@/features/medical-acts/ui/MedicalActsSection'
import { useMedicalActsLicenseEnabled } from '@/features/medical-acts/model/useMedicalActsLicenseEnabled'

const reviewExpertiseSchema = yup.object({
  conclusion: yup.string().required(),
  diagnostic: yup.string().required(),
})

type ReviewExpertiseFormValues = yup.InferType<typeof reviewExpertiseSchema>

function isExpertiseOpen(status: ExpertiseStatus) {
  if (status === ExpertiseStatus.Closed || status === ExpertiseStatus.Reviewed) {
    return false
  }
  return true
}

export default function ExpertisePage() {
  const navigate = useNavigate()
  const { expertiseId } = useParams()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const isEndingExpertise = useRef<boolean>(false)

  const authUser = useAppSelector(selectAuthUser)
  const hasMedicalActsLicense = useMedicalActsLicenseEnabled()

  const { data: expertise, isLoading: isFetchingExpertise } = useGetExpertiseQuery(expertiseId ?? skipToken, {
    refetchOnMountOrArgChange: true,
  })
  const [diagnoseExpertise, { isLoading: isDiagnosingExpertise }] = useDiagnoseExpertiseMutation()
  const [updateExpertise, { isLoading: isUpdatingExpertise }] = useUpdateExpertiseMutation()

  const rhForm = useForm<ReviewExpertiseFormValues>({
    values: { conclusion: expertise?.conclusion ?? '', diagnostic: expertise?.diagnostic ?? '' },
    resolver: yupResolver(reviewExpertiseSchema),
  })

  const generateFspRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' })
      toggleGenerateFSPDialog()
    },
    onError: () => enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectExpertiseCreateRequestTracker,
  })

  const [generateFSPDialogOpen, setGenerateFSPDialogOpen] = useState<boolean>(false)
  const [elaraChatOpen, setElaraChatOpen] = useState<boolean>(false)
  const [elaraSessionId, setElaraSessionId] = useState<string | null>(null)

  const initialFSPValues = useMemo(() => {
    return {
      Acts: [
        authUser?._id === expertise?.createdBy._id
          ? {
              code: 'RQD',
              price: 10,
              date: new Date().toISOString(),
            }
          : {
              code: 'TE2',
              price: 20,
              date: new Date().toISOString(),
            },
      ],
    }
  }, [expertise?.createdBy, authUser])

  const isExpertiseAssociatedWithExternalService = useMemo(() => _.includes([ExpertiseStatus.Omnidoc], expertise?.status), [expertise])

  const toggleGenerateFSPDialog = () => {
    setGenerateFSPDialogOpen(!generateFSPDialogOpen)
  }

  const handleGenerateFSP = (_actIds: string[], legacyActs: import('@/core/models/consultation.model').Act[]) => {
    if (!expertiseId) return
    generateFspRequestHandler.dispatch(generateFSP(expertiseId, legacyActs))
  }

  useEffect(() => {
    if (expertise && authUser) {
      const { redirect, url } = redirectTo(authUser, expertise)
      if (redirect) navigate(url)
    }
  }, [expertise, authUser, navigate])

  const handleEndExpertise = async () => {
    if (!expertiseId) return
    try {
      isEndingExpertise.current = true
      await updateExpertise({ id: expertiseId, status: ExpertiseStatus.Closed }).unwrap()
      isEndingExpertise.current = false
      enqueueSnackbar(t('notification.expertise.end.success'), { variant: 'success' })
    } catch (error) {
      isEndingExpertise.current = false
      enqueueSnackbar(t('notification.expertise.end.error'), { variant: 'error' })
    }
  }

  const handlePlanConsultation = () => {
    if (expertise) {
      navigate(`/consultations/create?patient=${expertise.patient._id}`)
    }
  }

  const handleSubmitReview: SubmitHandler<ReviewExpertiseFormValues> = async (values) => {
    if (!expertiseId) return
    try {
      await diagnoseExpertise({ id: expertiseId, conclusion: values.conclusion, diagnostic: values.diagnostic }).unwrap()
      enqueueSnackbar(t('notification.expertise.submitReview.success'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(t('notification.expertise.submitReview.error'), { variant: 'error' })
    }
  }

  if (isFetchingExpertise) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center">
        <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
      </Stack>
    )
  }

  if (!expertise) {
    return <NotFoundPage />
  }

  return (
    <Container maxWidth="xl" sx={{ height: { md: `calc(100vh - ${MAIN_HEADER_HEIGHT}px)` } }}>
      <Box pt="30px" pb="30px" height="100%">
        <Card sx={{ borderRadius: 3, height: '100%' }}>
          <Stack sx={{ height: '100%' }}>
            {/* Responsive header */}
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" p={3}>
              <Stack direction="row" alignItems="center" spacing={3} sx={{ flex: 1 }}>
                <Typography variant="h2" component="div" noWrap sx={{ fontSize: '24px', fontWeight: 700 }}>
                  {t('page.expertise.title')}
                </Typography>
                {isExpertiseOpen(expertise.status) && expertise.chatRoom && (
                  <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={() => setElaraChatOpen(true)} size="small">
                    {t('page.expertise.askElara.button')}
                  </Button>
                )}
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="end" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
                <ExpertiseHeader
                  isDoctor={expertise.medecin?._id === authUser?._id}
                  speciality={expertise.speciality}
                  status={expertise.status}
                  startDate={expertise.startDate}
                  endDate={expertise.endDate}
                />
                {expertise.status === ExpertiseStatus.Omnidoc && (
                  <Tooltip placement="top" title={t('page.expertise.openInNewTabButton.tooltip', { service: _.capitalize(ExpertiseService.Omnidoc) })}>
                    <IconButton
                      onClick={() => {
                        const redirectUrl = expertise.externalServices[ExpertiseService.Omnidoc]?.redirectUrl
                        if (!!redirectUrl) window.open(redirectUrl)
                      }}
                    >
                      <OpenInNewRoundedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton onClick={() => navigate('/expertises')} data-testid="closing-button" size="small">
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
            <Divider />
            {/* Responsive main content */}
            <Box sx={{ flex: '1 1 auto', pl: 3, pr: 3, pb: 2, overflowY: 'auto' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={7} md={8} xl={9}>
                  <Grid container pt="10px" spacing={3}>
                    <Grid item xs={12} lg={6}>
                      <InitiatorInfos who="initiator" staff={expertise.createdBy} />
                    </Grid>
                    {expertise.medecin && (
                      <Grid item xs={12} lg={6}>
                        <InitiatorInfos who="required" staff={expertise.medecin} />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <HealthDataList readOnly />
                    </Grid>
                    <Grid item xs={12}>
                      <Documents readOnly={isExpertiseOpen(expertise.status) || expertise.createdBy._id === authUser?._id} />
                    </Grid>
                    <Grid item xs={12}>
                      <ExpertiseForms
                        expertiseId={expertiseId!}
                        readOnly={isExpertiseOpen(expertise.status) || expertise.createdBy._id === authUser?._id}
                        showTitle
                      />
                    </Grid>
                    {hasMedicalActsLicense && (
                      <Grid item xs={12}>
                        <MedicalActsSection patientId={expertise.patient._id} expertiseId={expertiseId} readOnly={false} showTitle />
                      </Grid>
                    )}
                    {isStaff(authUser) && (
                      <>
                        <Grid item xs={12}>
                          <Stack direction="column" justifyContent="flex-end" alignItems="start" spacing={2}>
                            <Typography variant="h3" component="div" noWrap sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
                              {t('page.expertise.diagnostic.title')}
                            </Typography>
                            <Stack direction="row" spacing={2} width="100%">
                              <Box flex={1}>
                                <TextInputController
                                  control={rhForm.control}
                                  disabled={authUser.status !== 'MEDECIN'}
                                  fullWidth
                                  inputProps={{ 'data-testid': 'diagnostic-textarea' }}
                                  maxRows={6}
                                  minRows={4}
                                  multiline
                                  name="diagnostic"
                                />
                              </Box>
                            </Stack>
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <Stack direction="column" justifyContent="flex-end" alignItems="start" spacing={2}>
                            <Typography variant="h3" component="div" noWrap sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
                              {t('page.expertise.conclusion.title')}
                            </Typography>
                            <Stack direction="row" spacing={2} width="100%">
                              <Box flex={1}>
                                <TextInputController
                                  control={rhForm.control}
                                  disabled={authUser.status !== 'MEDECIN'}
                                  fullWidth
                                  inputProps={{ 'data-testid': 'conclusion-textarea' }}
                                  maxRows={6}
                                  minRows={4}
                                  multiline
                                  name="conclusion"
                                />
                              </Box>
                            </Stack>
                          </Stack>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="h3" component="div" sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
                          {t('page.expertise.message.title')}
                        </Typography>
                      </Stack>
                      <Messenger expertiseStatus={expertise.status} chatRoomId={expertise.chatRoom} />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={5} md={4} xl={3}>
                  <Box sx={{ position: 'sticky', top: 0, pt: '10px' }}>
                    <PatientInfosCard patient={expertise.patient} />
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Divider />
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" pt={2} pb={2} pl={3} pr={3} spacing={3}>
              {authUser?._id === expertise.createdBy._id && isExpertiseOpen(expertise.status) && !isExpertiseAssociatedWithExternalService && (
                <Button color="primary" component={Link} to={`/expertises/${expertise._id}/update`} variant="contained">
                  {t('common:button.modify.label')}
                </Button>
              )}
              {expertise.medecin?._id === authUser?._id && isExpertiseOpen(expertise.status) && (
                <LoadingButton color="info" loading={isDiagnosingExpertise} onClick={rhForm.handleSubmit(handleSubmitReview)} variant="contained">
                  {t('page.expertise.submitReviewButton.label')}
                </LoadingButton>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="end" spacing={3} sx={{ flex: 1 }}>
                <Tooltip title={t('page.expertise.documents.generateFSPButton.tooltip')} placement="top">
                  <Button onClick={toggleGenerateFSPDialog} data-testid="generate-fsp-button" variant="contained">
                    <ReceiptIcon />
                  </Button>
                </Tooltip>
                {isExpertiseOpen(expertise.status) && !isExpertiseAssociatedWithExternalService && (
                  <Button onClick={handlePlanConsultation} variant="contained" color="success">
                    {t('page.expertise.planConsultationButton.label')}
                  </Button>
                )}
                {isExpertiseOpen(expertise.status) && (
                  <LoadingButton loading={isEndingExpertise.current && isUpdatingExpertise} onClick={handleEndExpertise} variant="contained" color="error">
                    {t('page.expertise.endExpertise')}
                  </LoadingButton>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Card>
        <GenerateFspDialog
          initialValues={initialFSPValues}
          isGenerating={generateFspRequestHandler.requestTracker.loading}
          onClose={toggleGenerateFSPDialog}
          onGenerateFSP={handleGenerateFSP}
          open={generateFSPDialogOpen}
        />
        <ChatModal
          open={elaraChatOpen}
          onClose={() => setElaraChatOpen(false)}
          sessionId={elaraSessionId}
          onSessionIdChange={setElaraSessionId}
          titleKey="page.expertise.elaraChat.title"
          agentType="expertise"
          context={expertise ? { expertiseId: expertise._id, patientId: expertise.patient._id } : undefined}
        />
      </Box>
    </Container>
  )
}
